<template>
  <GeneralListComponent title="Tariff">
    <template #list>
      <v-row>
        <v-col cols="12">
          <!-- Tabs -->
          <v-tabs v-model="tab" centered fixed-tabs>
            <v-tabs-slider color="white"></v-tabs-slider>
            <v-tab> Active </v-tab>
            <v-tab> Closed </v-tab>
          </v-tabs>
        </v-col>

        <v-col cols="12" md="6" lg="4">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchContractList"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="1">
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                color="primary"
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
              >
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </template>

            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
        </v-col>

        <v-col cols="12" md="1" lg="3"></v-col>

        <v-col cols="12" md="4">
          <GeneralColumnToShow
            :allHeaders="headers"
            itemText="headerName"
            itemValue="field"
            :selectedHeaders="headerSelected"
            @headersChanged="headersChanged"
          />
        </v-col>

        <v-col cols="12">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 388px)"
            :class="
              $vuetify.theme.dark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'
            "
            id="myGrid"
            :columnDefs="headersForTable"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allContractList"
          ></AgGridVue>
        </v-col>

        <v-col v-if="totalItems > 0" cols="12" class="d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>

      <DetailsParent :resizable="true" v-model="displayDrawer">
        <template #details>
          <TransporterContractDetails :contractId="selectedContractId" />
        </template>
      </DetailsParent>

      <TransporterContractFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(10)"
      />
    </template>
  </GeneralListComponent>
</template>

<script>
import GeneralColumnToShow from "@/components/General/ColumnToShow.vue";
import GeneralListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import TransporterContractDetails from "@/components/ModuleBased/details/TransporterContractDetails.vue";
import UserContractButton from "@/components/AgGridComponents/UserContractButton.vue";
import TransporterContractFilters from "@/components/ModuleBased/dialogs/filters/TransporterContractFilters.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    GeneralListComponent,
    GeneralColumnToShow,
    AgGridVue,
    Pagination,
    DetailsParent,
    TransporterContractDetails,
    agColumnHeader: CustomHeader,
    UserContractButton,
    TransporterContractFilters,
  },
  data() {
    return {
      sorting: {},
      headers: [
        { headerName: "Tariff ID", field: "contract_id", sortable: true },
        {
          headerName: "Lane",
          field: "transporter_contract_lane_title",
          tooltipField: "transporter_contract_lane_title",
          sortable: true,
          minWidth: 800,
        },
        {
          headerName: "Start Date",
          field: "start_date",
          sortable: true,
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value);
          },
        },
        {
          headerName: "End Date",
          field: "end_date",
          sortable: true,
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value);
          },
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "UserContractButton",
        },
      ],
      allContractList: [],

      // details vars
      selectedContractId: null,
      displayDrawer: false,

      // pagination vars
      pageNo: 1,
      itemsPerPage: 10,
      totalItems: 100,

      // Filter vars
      filters: {},
      showFilterDialog: false,

      headerSelected: [],
      tab: null,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    headersForTable() {
      return [...this.headerSelected];
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  watch: {
    tab() {
      this.getContractList({ offset: 0, limit: this.itemsPerPage });
      this.displayDrawer = false;
    },
  },
  methods: {
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(10);
    },
    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(10);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchContractList() {
      this.pageNo = 1;
      this.getContractList();
    },
    headersChanged(value) {
      this.headerSelected = value;
      this.resizeGrid();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getContractList();
    },
    prevPage() {
      this.pageNo--;
      this.getContractList();
    },
    nextPage() {
      this.pageNo++;
      this.getContractList();
    },
    getContractList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        transporter: this.currentProfile.company,
        offset: this.offset,
        limit: this.itemsPerPage,
        status: this.tab == 0 ? "Active" : "Closed",
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.transporterContract
        .getTransporterContractList(params)
        .then((res) => {
          this.allContractList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    viewContract(id) {
      this.selectedContractId = id;
      this.displayDrawer = true;
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("contract");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.getContractList();
      }
    }
  },
};
</script>